import React, { useState, useEffect } from 'react'
import { Button, Dropdown, Input } from 'components/UIElements'
import { closeIcon } from 'assets/assets'
import { DURATION_TYPE_MAP, MODAL_CONTENT_MAP } from 'utils/constants'
import { convertMinToDays } from 'utils/time'
import STRINGS from 'utils/strings'
import { generateNotyMessage } from 'utils/request'
import {
  convertCycleOccurToDays,
  convertCycleOccurToMinutes,
  convertCycleTimeToDays,
  convertCycleTimeToMinutes,
  formatCycleAnchorVisitOptions,
  getCycleAnchorEligibleVisits,
  getCycleStudyDurationValidationError,
  normalizeCycleAnchorOption,
} from '../../utils/visitDayUtils'

const initalValues = ({
  vcName,
  cycleDuration = 0,
  cycleOccur = 0,
  afterVisitCycleValue = '',
  afterCycle = 'cycle',
}) => ({
  vcName,
  cycleDuration,
  cycleDurationTime: 'days',
  cycleOccur,
  cycleOccurTime: 'days',
  afterCycle,
  afterVisitCycleValue,
})

const OPTIONS = [
  {
    key: 'cycle',
    text: 'cycle',
  },
  {
    key: 'visit',
    text: 'visit',
  },
  {
    key: 'participant_start',
    text: 'participant start',
  },
]

const VisitCycleModal = props => {
  const {
    disabled,
    cycleName,
    cycleData,
    phasesList,
    onClose,
    onAddNewCycle,
    onEditExistingCycle,
    allCycles,
    setSearchValue,
    isReadOnlyState,
    studyDuration,
    showError,
  } = props

  const isEdit = !!cycleData?.name
  const defaultCycleDuration =
    typeof studyDuration === 'number' && studyDuration >= 1 && studyDuration <= 28
      ? studyDuration
      : 28

  const getCycleDurationInDays = (value, timeUnit) => convertCycleTimeToDays(value, timeUnit)

  const getConfiguredCycles = () =>
    phasesList.reduce((acc, phase) => [...acc, ...(phase?.cycles || [])], []).filter(cycle => !cycle?.is_dummy)

  const [errors, setErrors] = useState({
    vcName: '',
    cycleDuration: '',
    cycleOccur: '',
  })

  const [isCloseModal, setCloseModal] = useState(false)

  const [visitCycle, setVisitCycle] = useState(
    initalValues({
      vcName: cycleName,
      cycleDuration: convertMinToDays(cycleData?.length_value, false) || (cycleData?.length_value === 0 ? 1 : defaultCycleDuration),
      cycleOccur: convertMinToDays(cycleData?.start_time, false) || (cycleData?.start_time === 0 ? 0 : 1),
      afterVisitCycleValue: cycleData?.cycle_anchor_id,
      afterCycle: cycleData?.metadata?.anchor_option,
    }),
  )

  const [cycleDurationTimeOptions, setCycleDurationTimeOptions] = useState(
    Object.keys(DURATION_TYPE_MAP).map(key => ({ key, text: key })),
  )

  const anchorEligibleVisits = getCycleAnchorEligibleVisits(phasesList)
  const nonCycledVisits = formatCycleAnchorVisitOptions(anchorEligibleVisits)

  const {
    vcName,
    cycleDuration,
    cycleDurationTime,
    cycleOccur,
    cycleOccurTime,
    afterCycle,
    afterVisitCycleValue,
  } = visitCycle

  const updateVC = ({ key, value }) => {
    setVisitCycle(prev => ({ ...prev, [key]: value }))
  }

  useEffect(() => {
    return () => {
      setSearchValue?.('')
    }
  }, [setSearchValue])

  useEffect(() => {
    if (!afterVisitCycleValue) {
      if (afterCycle === 'cycle' && !!allCycles?.length) {
        updateVC({ key: 'afterVisitCycleValue', value: allCycles?.[allCycles?.length - 1]?.key })
      }
      if (afterCycle === 'visit' && !!nonCycledVisits?.length) {
        updateVC({ key: 'afterVisitCycleValue', value: nonCycledVisits?.[nonCycledVisits?.length - 1]?.key })
      }
    }
  }, [afterVisitCycleValue, afterCycle, allCycles, nonCycledVisits])

  useEffect(() => {
    if (afterCycle === 'cycle') {
      if (!allCycles?.length && !nonCycledVisits?.length) {
        updateVC({ key: 'afterCycle', value: 'participant_start' })
      } else if (!allCycles?.length) {
        setVisitCycle(prev => ({ ...prev, afterCycle: 'visit', afterVisitCycleValue: nonCycledVisits?.[0].key }))
      } else if (!nonCycledVisits?.length) {
        setVisitCycle(prev => ({ ...prev, afterCycle: 'cycle', afterVisitCycleValue: allCycles?.[0].key }))
      }
    }
  }, [afterCycle, nonCycledVisits, allCycles])

  const getAnchorVisitStudyDay = anchorVisitId => {
    const visit = anchorEligibleVisits.find(
      v => String(v?.cycle_id) === String(anchorVisitId) || String(v?.id) === String(anchorVisitId),
    )
    const raw = visit?.metadata?.computed_study_day ?? visit?.day
    return Number.isFinite(Number(raw)) ? Number(raw) : 0
  }

  const validateDataCycleFields = () => {
    const validateFields = [vcName, cycleDuration, cycleOccur]

    if (!allCycles?.length) validateFields.pop()

    if (validateFields.includes('')) {
      if (!vcName) {
        setErrors({ ...errors, vcName: STRINGS.cannotBeEmpty })
      }
      if (cycleDuration === undefined || cycleDuration === null || cycleDuration === '') {
        setErrors({ ...errors, cycleDuration: STRINGS.cannotBeEmpty })
      }
      if (cycleOccur === undefined || cycleOccur === null || cycleOccur === '') {
        setErrors({ ...errors, cycleOccur: STRINGS.cannotBeEmpty })
      }
      return
    }

    const length_value = convertCycleTimeToMinutes(cycleDuration, cycleDurationTime)
    const start_time = convertCycleOccurToMinutes(cycleOccur, cycleOccurTime)
    const durationInDays = getCycleDurationInDays(cycleDuration, cycleDurationTime)
    const occurInDays = convertCycleOccurToDays(cycleOccur, cycleOccurTime)
    const existingCycles = getConfiguredCycles().filter(
      cycle => !isEdit || String(cycle.id) !== String(cycleData?.id),
    )
    const normalizedAnchor = normalizeCycleAnchorOption(afterCycle, afterVisitCycleValue)

    const studyDurationErr = getCycleStudyDurationValidationError({
      studyDurationDays: studyDuration,
      durationDays: durationInDays,
      occurDays: occurInDays,
      anchorOption: normalizedAnchor,
      anchorCycleId: afterVisitCycleValue,
      anchorVisitStudyDay: normalizedAnchor === 'visit' ? getAnchorVisitStudyDay(afterVisitCycleValue) : undefined,
      existingCycles,
      lengthValueMinutes: Number(length_value || 0),
      cycleId: cycleData?.id,
      errorMessageTemplate: STRINGS.cycleDurationExceedsStudyDuration,
    })

    if (studyDurationErr) {
      showError({
        text: generateNotyMessage(studyDurationErr, false),
      })
      return
    }

    onClose()

    const getAnchorId = () => {
      if (afterCycle === 'participant_start') {
        return ''
      }

      if (afterCycle === 'visit') {
        return afterVisitCycleValue || ''
      }

      return afterVisitCycleValue || ''
    }

    if (isEdit) {
      const updatedVisitCycle = {
        name: vcName,
        length_value: Number(length_value || 0),
        start_time: start_time,
        // start_window: 0,
        qualifying_criteria: {},
        metadata: { anchor_option: afterCycle },
        anchor_id: getAnchorId(),
      }

      onEditExistingCycle(updatedVisitCycle)
    } else {
      const newVisitCycle = {
        name: vcName,
        metadata: { anchor_option: afterCycle },
        qualifying_criteria: {},
        length_value,
        start_time,
        start_window: 0,
        anchor_id: getAnchorId(),
      }

      onAddNewCycle(newVisitCycle)
    }
  }

  const getCycleOptions = () => {
    const noCycles = !allCycles?.length
    const noNonCycledVisits = !nonCycledVisits?.length

    let options = OPTIONS

    if (noCycles) {
      options = options.filter(option => option.key !== 'cycle')
    }

    if (noNonCycledVisits) {
      options = options.filter(option => option.key !== 'visit')
    }

    return options
  }

  const getLastVisitCycleOptions = () => {
    if (afterCycle === 'visit') {
      return nonCycledVisits
    }

    return allCycles
  }

  const onExit = () => {
    setCloseModal(true)
  }

  const closeModalUI = () => (
    <div>
      <h6 className='label-large light-grey'>{MODAL_CONTENT_MAP.pleaseConfirm}</h6>
      <div className='top'>
        <div>
          <div className='confirmation-statement'>{MODAL_CONTENT_MAP.closeOut}</div>
          <p>{STRINGS.loseUnsavedContent}</p>
        </div>
      </div>
      <Button
        disabled={disabled}
        grey
        id='modal-cancel'
        content={'Cancel'}
        onClick={() => {
          setCloseModal(false)
        }}
      />
      <Button
        disabled={disabled}
        id='modal-confirm'
        className={`confirm`}
        content={'Confirm'}
        onClick={() => {
          onClose()
        }}
      />
    </div>
  )

  return (
    <div className='visit-cycle-modal'>
      <Button
        className='modal-close-x'
        disabled={disabled}
        onClick={isCloseModal ? () => setCloseModal(false) : onExit}
        content={closeIcon()}
      />
      {isCloseModal ? (
        closeModalUI()
      ) : (
        <>
          <Input
            label='VISIT CYCLE NAME'
            disabled={disabled || isReadOnlyState}
            id='visit-cycle-name-input'
            className='visit-cycle-name-input'
            value={vcName || ''}
            onChange={value => {
              updateVC({ key: 'vcName', value })
              setErrors({ ...errors, vcName: '' })
            }}
            errorText={errors?.vcName}
            hasError={!!errors?.vcName}
          />
          <label className='label-small' htmlFor='visit-day-selection'>
            CYCLE DURATION
          </label>
          <div className='flexed cycle-duration'>
            <Input
              disabled={disabled || isReadOnlyState}
              id='visit-cycle-duration-input'
              min='1'
              onChange={value => {
                if (Number(value) === 0) {
                  updateVC({ key: 'cycleDuration', value: 1 })
                } else {
                  updateVC({ key: 'cycleDuration', value: value || 1 })
                }
                setErrors({ ...errors, cycleDuration: '' })
              }}
              onBlur={() => {
                if (Number(cycleDuration) === 0) {
                  updateVC({ key: 'cycleDuration', value: 1 })
                }
              }}
              value={cycleDuration || 1}
              type='number'
              errorText={errors?.cycleDuration}
              hasError={!!errors?.cycleDuration}
            />
            <Dropdown
              disabled={disabled || isReadOnlyState}
              id='visit-cycle-duration-selector'
              options={cycleDurationTimeOptions}
              onSelect={item => {
                updateVC({ key: 'cycleDurationTime', value: item.key })
                setErrors({ ...errors, cycleDuration: '' })
              }}
              selected={cycleDurationTime}
            />
          </div>

          <label className='label-small' htmlFor='visit-day-selection'>
            WHEN DOES CYCLE OCCUR
          </label>
          <div className='flexed cycle-duration'>
            <Input
              disabled={disabled || isReadOnlyState}
              id='visit-cycle-occur-input'
              min='0'
              onChange={value => {
                updateVC({ key: 'cycleOccur', value: value || 0 })
                setErrors({ ...errors, cycleOccur: '' })
              }}
              value={cycleOccur || 0}
              type='number'
              errorText={errors?.cycleOccur}
              hasError={!!errors?.cycleOccur}
            />
            <Dropdown
              disabled={disabled || isReadOnlyState}
              id='visit-cycle-occur-selector'
              options={cycleDurationTimeOptions}
              onSelect={item => updateVC({ key: 'cycleOccurTime', value: item.key })}
              selected={cycleOccurTime}
            />
            <span>after</span>
            <Dropdown
              disabled={disabled || isReadOnlyState}
              id='visit-cycle-after-selector'
              options={getCycleOptions()}
              onSelect={item => {
                if (item.key === 'cycle') {
                  setVisitCycle(prev => ({ ...prev, afterCycle: item.key, afterVisitCycleValue: allCycles?.[0].key }))
                }

                if (item.key === 'visit') {
                  setVisitCycle(prev => ({ ...prev, afterCycle: item.key, afterVisitCycleValue: nonCycledVisits?.[0].key }))
                }

                if (item.key === 'participant_start') {
                  updateVC({ key: 'afterCycle', value: item.key })
                }
              }}
              selected={afterCycle}
              className='visit-cycle-after-selector'
            />
          </div>

          {afterCycle !== 'participant_start' && (
            <div className='visit-cycle-selector'>
              <Dropdown
                disabled={disabled || isReadOnlyState}
                id='last-visit-cycle-selector'
                options={getLastVisitCycleOptions()}
                onSelect={item => updateVC({ key: 'afterVisitCycleValue', value: item.key })}
                selected={afterVisitCycleValue}
              />
            </div>
          )}

          {!isReadOnlyState && (
            <div className='flexed end-justified confirmation'>
              <Button
                id='modal-confirm'
                className='confirm'
                content={`Confirm${!!cycleData?.name ? ' and change' : ''}`}
                onClick={() => {
                  validateDataCycleFields()
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

VisitCycleModal.propTypes = {}

export default VisitCycleModal
